// LRPO-5164 — Guided onboarding tour for the Live Command Center.
//
// A lightweight, zero-dependency overlay tour driven by Stimulus.
// Renders a dim backdrop with a spotlight on the current step's target
// element and a tooltip (title/body + Next/Back/Skip/Finish + step counter).
//
// Steps map to the 5 core panels:
//   1. KPI cards     — #live-tour-kpi-cards
//   2. Live Scan Feed — #live-tour-scan-feed
//   3. Scanner Activity — #live-tour-scanner-activity
//   4. Target/Goal   — #live-tour-goal-strip
//   5. Hourly Velocity — #live-tour-hourly-velocity
//
// If a step's anchor element is absent (panel hidden / event not live),
// the step is silently skipped.
//
// Scroll lock: the page can't be scrolled while the tour is active (it would
// drag the spotlighted panel out from under the punched-out hole, since the
// spotlight position is a snapshot, not continuously re-measured). The REAL
// scroller on the LCC is #app-wrapper (application.html.erb) — NOT <body>,
// which is `vh-100 overflow-hidden` and never scrolls, which is why the earlier
// body-only lock (07-07) never stopped it. We therefore (a) lock #app-wrapper
// with `overflow-y: hidden !important` (an inline !important is the only thing
// that beats the layout's own `#app-wrapper { overflow-y: scroll !important }`),
// AND (b) add container-agnostic CAPTURING guards on wheel / touchmove / scroll
// keys so nothing scrolls regardless of which element actually scrolls. Locked
// on open, fully restored (overflow + scrollTop + guards) on every exit path —
// see _lockScroll/_unlockScroll.
//
// Dismissal: on Finish (and optionally Skip), POSTs to the existing
// exhibitor_onboarding_banner_dismissals endpoint so the banner + CTA
// don't reappear (per-user, cross-device, stored in DB via
// ExhibitorUserBannerDismissal). Falls back to localStorage if the
// dismissalUrl value is not provided.
import { Controller } from "@hotwired/stimulus"

const STEPS = [
  {
    anchor:  "live-tour-kpi-cards",
    title:   "KPI Cards",
    body:    "A real-time snapshot of total leads, today's count, active scanners, and lead rate — the numbers update as scans come in."
  },
  {
    anchor:  "live-tour-scan-feed",
    title:   "Live Scan Feed",
    body:    "Every new lead scan appears here instantly. Pause the feed to review entries without missing incoming scans."
  },
  {
    anchor:  "live-tour-scanner-activity",
    title:   "Scanner Activity",
    body:    "See which scanners are active (last 15 min), idle (15–60 min), or inactive — great for spotting who needs support on the floor."
  },
  {
    anchor:  "live-tour-goal-strip",
    title:   "Target",
    body:    "Set a lead target for the market. The progress bar and percentage track your team's pace toward it in real time."
  },
  {
    anchor:  "live-tour-hourly-velocity",
    title:   "Hourly Velocity",
    body:    "A bar chart of leads captured per hour today — use it to spot quiet stretches and peak scanning windows."
  }
]

// Margin (px) added around the spotlight rect so the glow ring sits
// outside the element rather than clipping its edge.
const SPOTLIGHT_PADDING = 8
// Pixels between the spotlight edge and the tooltip box.
const TOOLTIP_OFFSET = 16

// Stable element IDs so the dialog can programmatically associate its
// accessible name (title + step counter) and description (body) for screen
// readers via aria-labelledby / aria-describedby.
const TITLE_ID   = "live-tour-tooltip-title"
const BODY_ID    = "live-tour-tooltip-body"
const COUNTER_ID = "live-tour-tooltip-counter"

// Tooltip positioning fallbacks / clamps (px). Used before the tooltip has
// been measured (offsetWidth/Height are 0 until laid out).
const TOOLTIP_EST_HEIGHT = 180   // estimated card height pre-measure
const TOOLTIP_EST_WIDTH  = 320   // estimated card width pre-measure
const VIEWPORT_MARGIN_X  = 12    // min horizontal gap from the viewport edge
const VIEWPORT_MARGIN_Y  = 8     // min vertical gap from the viewport top

// Keys whose default browser action scrolls the page. A capturing keydown guard
// preventDefaults these while the tour is active so keyboard scrolling can't
// drag the spotlighted panel away. Space is listed but SKIPPED when a tooltip
// control is focused (there Space activates the button, it doesn't scroll).
// "Spacebar" is the legacy IE/Edge key value for the space bar.
const SCROLL_KEYS = new Set([
  "PageUp", "PageDown", "Home", "End",
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
  " ", "Spacebar"
])

export default class extends Controller {
  static values = {
    // POST URL for the dismissal endpoint (exhibitor_onboarding_banner_dismissals_path).
    // Passed in by the onboarding banner partial via data-live-tour-dismissal-url-value.
    dismissalUrl:  { type: String, default: "" },
    bannerKey:     { type: String, default: "live_command_center_welcome" },
    csrfToken:     { type: String, default: "" }
  }

  connect() {
    this._overlay   = null
    this._spotlight = null
    this._tooltip   = null
    this._stepIndex = 0
    this._steps     = []        // filtered steps (missing anchors removed)
    this._rafId     = null      // pending requestAnimationFrame id (cancel on teardown)
    this._returnFocusEl = null  // element to restore focus to when the tour closes
    // Prior inline overflow values, saved by _lockScroll so _unlockScroll can
    // restore exactly what was there rather than clobbering it to "auto".
    // Null doubles as "not currently locked" — see _unlockScroll.
    this._priorHtmlOverflow = null
    this._priorBodyOverflow = null
    // Real scroll container (#app-wrapper) lock state — see _lockScroll. The
    // LCC's scroll lives on #app-wrapper, NOT body/html, so the body-level lock
    // alone (07-07) never stopped it. Null == "not currently locked".
    this._lockedContainer            = null
    this._priorContainerOverflow     = null
    this._priorContainerOverflowPrio = null
    this._priorContainerScrollTop    = null
    this._bound = {
      onKey:       this._onKey.bind(this),
      onResize:    this._onResize.bind(this),
      onTouchMove: this._onTouchMove.bind(this),
      onWheel:     this._onWheel.bind(this),
      onScrollKey: this._onScrollKey.bind(this)
    }
  }

  disconnect() {
    this._teardown()
  }

  // Public entry point — called via data-action from the "Take a tour" CTA.
  start(event) {
    // Build the filtered step list once per tour run (anchors may become
    // available only after Turbo navigation, so resolve lazily).
    this._steps = STEPS.filter(s => document.getElementById(s.anchor) !== null)
    if (this._steps.length === 0) return

    // Remember where focus was so we can restore it when the tour closes.
    // Prefer the element that triggered the tour (the CTA button); fall back
    // to the active element. The CTA itself is removed on dismiss, so
    // _teardown resolves a meaningful landmark as a final fallback.
    this._returnFocusEl =
      (event && event.currentTarget) ||
      (document.activeElement instanceof HTMLElement ? document.activeElement : null)

    this._stepIndex = 0
    this._build()
    this._render()
  }

  // -----------------------------------------------------------------------
  // Tooltip button actions (wired via direct click listeners in
  // _buildTooltipHeader / _buildTooltipFooter — NOT Stimulus data-action,
  // because the overlay is appended to document.body, outside this
  // controller's element subtree, where Stimulus never binds actions).
  // -----------------------------------------------------------------------

  next() {
    this._stepIndex++
    if (this._stepIndex >= this._steps.length) {
      this._finish()
    } else {
      this._render()
    }
  }

  back() {
    if (this._stepIndex > 0) {
      this._stepIndex--
      this._render()
    }
  }

  skip() {
    this._dismiss()
    this._teardown()
  }

  // -----------------------------------------------------------------------
  // Private
  // -----------------------------------------------------------------------

  _build() {
    this._lockScroll()

    // Overlay — full-viewport dim layer. role="dialog" + aria-modal hides
    // background content from assistive tech; the dialog is labelled by the
    // step title and described by the step body (IDs set in _renderTooltip).
    this._overlay = document.createElement("div")
    this._overlay.className        = "live-tour-overlay"
    this._overlay.setAttribute("role", "dialog")
    this._overlay.setAttribute("aria-modal", "true")
    this._overlay.setAttribute("aria-roledescription", "guided tour")
    this._overlay.setAttribute("aria-label", "Live Command Center tour")
    this._overlay.setAttribute("aria-labelledby", `${TITLE_ID} ${COUNTER_ID}`)
    this._overlay.setAttribute("aria-describedby", BODY_ID)

    // Spotlight — transparent punch-out inside the SVG mask.
    // We use a CSS clip-path rectangle that updates per step.
    this._spotlight = document.createElement("div")
    this._spotlight.className = "live-tour-spotlight"

    // Tooltip card.
    this._tooltip = document.createElement("div")
    this._tooltip.className = "live-tour-tooltip"
    this._tooltip.setAttribute("role", "document")

    this._overlay.appendChild(this._spotlight)
    this._overlay.appendChild(this._tooltip)
    document.body.appendChild(this._overlay)

    // Keyboard + resize listeners.
    document.addEventListener("keydown", this._bound.onKey)
    window.addEventListener("resize",   this._bound.onResize)
  }

  _render() {
    const step   = this._steps[this._stepIndex]
    const anchor = document.getElementById(step.anchor)

    // The anchor can vanish mid-tour (e.g. a Turbo Stream broadcast removes
    // the panel). Guard against null before touching it — finish the tour
    // gracefully rather than throwing.
    if (!anchor) {
      this._finish()
      return
    }

    const total   = this._steps.length
    const current = this._stepIndex + 1
    const isLast  = this._stepIndex === total - 1

    // Scroll the anchor into view before computing rects. Use "instant" so the
    // getBoundingClientRect() read in the rAF callback reflects the final
    // scroll position — smooth scrolling takes ~300-500ms but the next frame
    // fires in ~16ms, which would read mid-scroll coordinates.
    anchor.scrollIntoView({ block: "nearest", behavior: "instant" })

    // Use rAF so layout settles before we read the rect. Cancel any pending
    // frame first so rapid Next/Back clicks don't stack callbacks.
    if (this._rafId !== null) cancelAnimationFrame(this._rafId)
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null
      // If the tour was torn down (ESC/Skip) during the frame delay, the
      // overlay and its child refs are null — bail before dereferencing them.
      if (!this._overlay) return
      this._positionSpotlight(anchor)
      this._renderTooltip(step, anchor, current, total, isLast)
      this._focusFirstButton()
    })
  }

  _positionSpotlight(anchor) {
    const r  = anchor.getBoundingClientRect()
    const p  = SPOTLIGHT_PADDING
    const x1 = r.left   - p
    const y1 = r.top    - p
    const x2 = r.right  + p
    const y2 = r.bottom + p

    // The spotlight is a transparent div with a huge box-shadow spread that
    // pushes the dark dim outward, leaving a "hole" over the anchor. The
    // overlay is position:fixed, so the spotlight (its child) is positioned in
    // viewport coordinates — exactly what getBoundingClientRect() returns. Do
    // NOT add window.scrollX/scrollY here, or a scrolled page offsets the
    // spotlight by the scroll distance from the actual panel.
    Object.assign(this._spotlight.style, {
      left:   `${x1}px`,
      top:    `${y1}px`,
      width:  `${x2 - x1}px`,
      height: `${y2 - y1}px`
    })
  }

  _renderTooltip(step, anchor, current, total, isLast) {
    // Build with DOM methods (not innerHTML) to avoid any XSS surface,
    // even though step copy is hardcoded. This also satisfies the
    // security-guidance plugin warning about innerHTML + untrusted content.
    const frag = document.createDocumentFragment()

    // Title + body carry stable IDs so the dialog's aria-labelledby /
    // aria-describedby (set in _build) announce the step to screen readers.
    const title = document.createElement("h6")
    title.id          = TITLE_ID
    title.className   = "live-tour-tooltip__title"
    title.textContent = step.title

    const body = document.createElement("p")
    body.id          = BODY_ID
    body.className   = "live-tour-tooltip__body"
    body.textContent = step.body

    frag.appendChild(this._buildTooltipHeader(current, total))
    frag.appendChild(title)
    frag.appendChild(body)
    frag.appendChild(this._buildTooltipFooter(isLast))

    this._tooltip.replaceChildren(frag)

    this._positionTooltip(anchor)
  }

  // Header row: step counter + Skip button. The counter shares the dialog's
  // accessible name (aria-labelledby) so its updates are announced on focus move.
  _buildTooltipHeader(current, total) {
    const header = document.createElement("div")
    header.className = "live-tour-tooltip__header"

    const counter = document.createElement("span")
    counter.id          = COUNTER_ID
    counter.className   = "live-tour-tooltip__counter"
    counter.textContent = `${current} of ${total}`

    const skipBtn = document.createElement("button")
    skipBtn.className      = "live-tour-tooltip__skip btn btn-link btn-sm p-0"
    // Direct listener (not Stimulus data-action): the overlay lives on
    // document.body, OUTSIDE this controller's element subtree, so Stimulus
    // never binds actions on these buttons (it only observes action elements
    // within the controller element). Buttons are recreated each render
    // (replaceChildren), so listeners GC with the removed nodes — no leak.
    // Mirrors the controller's existing raw-listener pattern for ESC/resize.
    skipBtn.addEventListener("click", () => this.skip())
    skipBtn.setAttribute("aria-label", "Skip tour")
    skipBtn.textContent    = "Skip"

    header.appendChild(counter)
    header.appendChild(skipBtn)
    return header
  }

  // Footer row: Back + Next/Finish buttons.
  _buildTooltipFooter(isLast) {
    const isFirst = this._stepIndex === 0

    const footer = document.createElement("div")
    footer.className = "live-tour-tooltip__footer"

    const backBtn = document.createElement("button")
    backBtn.className      = `btn btn-sm btn-outline-secondary live-tour-tooltip__back${isFirst ? " invisible" : ""}`
    // Direct listener — see the note in _buildTooltipHeader: the overlay is
    // outside the Stimulus controller subtree, so data-action never binds.
    backBtn.addEventListener("click", () => this.back())
    backBtn.textContent    = "Back"
    // Layout-invisible Back on step 1: take it out of the tab order so the
    // focus trap doesn't land on an invisible control.
    if (isFirst) backBtn.setAttribute("tabindex", "-1")

    const nextBtn = document.createElement("button")
    nextBtn.className = "btn btn-sm btn-primary live-tour-tooltip__next"
    // Always call `next` — on the last step, next() detects stepIndex >= steps.length
    // and calls _finish() internally. Calling `skip` here would route Finish through
    // the early-exit path instead of the completion path.
    // Direct listener — see the note in _buildTooltipHeader: the overlay is
    // outside the Stimulus controller subtree, so data-action never binds.
    nextBtn.addEventListener("click", () => this.next())
    nextBtn.textContent    = isLast ? "Finish" : "Next"

    footer.appendChild(backBtn)
    footer.appendChild(nextBtn)
    return footer
  }

  _positionTooltip(anchor) {
    const r     = anchor.getBoundingClientRect()
    const p     = SPOTLIGHT_PADDING
    const tipEl = this._tooltip
    const vpW   = window.innerWidth
    const vpH   = window.innerHeight

    // The tooltip is a child of the position:fixed overlay, so all coordinates
    // are viewport-relative — getBoundingClientRect() values are used as-is
    // (no window.scrollX/scrollY). Adding scroll offsets would misplace the
    // tooltip by the scroll distance on a scrolled page.
    const spotBottom = r.bottom + p
    const spotTop    = r.top    - p
    const tipH       = tipEl.offsetHeight || TOOLTIP_EST_HEIGHT   // measured or estimated

    // Place below the spotlight; if that overflows the viewport bottom, place above.
    let top
    if (spotBottom + TOOLTIP_OFFSET + tipH < vpH) {
      top = spotBottom + TOOLTIP_OFFSET
    } else {
      top = Math.max(VIEWPORT_MARGIN_Y, spotTop - TOOLTIP_OFFSET - tipH)
    }

    // Horizontally: centre over the anchor, clamped to the viewport edges.
    const anchorCX = r.left + r.width / 2
    const tipW     = tipEl.offsetWidth || TOOLTIP_EST_WIDTH
    let left = anchorCX - tipW / 2
    left = Math.max(VIEWPORT_MARGIN_X, Math.min(left, vpW - tipW - VIEWPORT_MARGIN_X))

    Object.assign(tipEl.style, { top: `${top}px`, left: `${left}px` })
  }

  _focusFirstButton() {
    const btn = this._tooltip && this._tooltip.querySelector("button:not([tabindex='-1'])")
    if (btn) btn.focus()
  }

  // All keyboard-focusable controls inside the dialog, in DOM order. The
  // tabindex="-1" Back button (step 1) is excluded so the trap never lands on
  // an invisible control.
  _focusableElements() {
    if (!this._tooltip) return []
    return Array.from(
      this._tooltip.querySelectorAll("button:not([tabindex='-1'])")
    )
  }

  // Trap Tab / Shift+Tab within the dialog so keyboard users can't reach the
  // background content the overlay visually hides (and aria-modal claims to
  // hide). Cycles between the first and last focusable control.
  _trapFocus(event) {
    const focusables = this._focusableElements()
    if (focusables.length === 0) {
      // Nothing focusable inside the dialog — keep focus on the dialog itself.
      event.preventDefault()
      return
    }

    const first  = focusables[0]
    const last   = focusables[focusables.length - 1]
    const active = document.activeElement

    if (event.shiftKey) {
      // Shift+Tab from the first control (or focus already outside) → wrap to last.
      if (active === first || !this._tooltip.contains(active)) {
        event.preventDefault()
        last.focus()
      }
    } else {
      // Tab from the last control (or focus outside) → wrap to first.
      if (active === last || !this._tooltip.contains(active)) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  _finish() {
    this._dismiss()
    this._teardown()
  }

  // Lock page scroll for the tour's lifetime. Without this the background
  // stays scrollable (the overlay/spotlight are pointer-events: none so
  // clicks and wheel/touch input pass straight through to it), which drags
  // the spotlighted panel out from under the punched-out hole.
  _lockScroll() {
    // (1) html/body lock. Belt-and-suspenders: on the LCC the body is
    // `vh-100 overflow-hidden` and never scrolls, but other browsers/pages may
    // scroll one or the other. Save whatever inline overflow was already set
    // (normally "") so _unlockScroll restores it exactly instead of clobbering
    // it to "auto".
    this._priorHtmlOverflow = document.documentElement.style.overflow
    this._priorBodyOverflow = document.body.style.overflow
    document.documentElement.style.overflow = "hidden"
    document.body.style.overflow            = "hidden"

    // (2) Lock the REAL scroll container. On the Live Command Center the page
    // scrolls inside #app-wrapper (application.html.erb: <body> is
    // `vh-100 overflow-hidden` and #app-wrapper is `flex-grow-1 overflow-auto`),
    // which is exactly why the 07-07 body-only lock did nothing. Only manage it
    // when it's a DISTINCT element (html/body are handled above). CRITICAL: the
    // layout pins `#app-wrapper { overflow-y: scroll !important }` in an inline
    // <style> block, so a plain `style.overflow = "hidden"` is OUTRANKED — set
    // it with the `important` priority (an inline !important beats an
    // author-stylesheet !important). Preserve scrollTop to restore on exit.
    const container = this._realScrollContainer()
    if (container && container !== document.documentElement && container !== document.body) {
      this._lockedContainer            = container
      this._priorContainerOverflow     = container.style.getPropertyValue("overflow-y")
      this._priorContainerOverflowPrio = container.style.getPropertyPriority("overflow-y")
      this._priorContainerScrollTop    = container.scrollTop
      container.style.setProperty("overflow-y", "hidden", "important")
    }

    // (3) Container-agnostic input guards. Even with overflow locked, block the
    // scroll GESTURES themselves — the overlay/spotlight are pointer-events:none
    // so wheel/touch/key input passes straight through to whatever scrolls.
    // Capturing + { passive: false } so preventDefault() runs before the
    // target's own handlers and actually cancels the scroll, whatever element it
    // targets. Removed on every exit path via _unlockScroll.
    window.addEventListener("wheel",     this._bound.onWheel,     { capture: true, passive: false })
    window.addEventListener("touchmove", this._bound.onTouchMove, { capture: true, passive: false })
    window.addEventListener("keydown",   this._bound.onScrollKey, { capture: true })

    // iOS Safari's touch-driven rubber-band ignores overflow:hidden, so also
    // block touchmove at the document level (preserves the prior coverage).
    // { passive: false } is required for preventDefault() to have any effect.
    document.addEventListener("touchmove", this._bound.onTouchMove, { passive: false })
  }

  // Restore whatever scroll state preceded the tour. Called unconditionally
  // from _teardown() — including the disconnect() path — so the lock can
  // never survive the tour (finish, skip, ESC, anchor-vanished, or a Turbo
  // navigation away mid-tour all funnel through _teardown()). Safe to call
  // when scroll was never locked: it just re-assigns "" and removes a
  // listener that was never added.
  _unlockScroll() {
    // (1) html/body.
    document.documentElement.style.overflow = this._priorHtmlOverflow || ""
    document.body.style.overflow            = this._priorBodyOverflow || ""
    this._priorHtmlOverflow = null
    this._priorBodyOverflow = null

    // (2) Real container: restore its exact prior overflow-y (value + priority).
    // Normally there was none inline (the scroll came from the stylesheet), so
    // remove the property to hand control back to the `!important` rule. Then
    // restore the pre-tour scroll position (the tour scrolled through panels).
    if (this._lockedContainer) {
      const c = this._lockedContainer
      if (this._priorContainerOverflow) {
        c.style.setProperty("overflow-y", this._priorContainerOverflow, this._priorContainerOverflowPrio || "")
      } else {
        c.style.removeProperty("overflow-y")
      }
      if (this._priorContainerScrollTop != null) c.scrollTop = this._priorContainerScrollTop
      this._lockedContainer            = null
      this._priorContainerOverflow     = null
      this._priorContainerOverflowPrio = null
      this._priorContainerScrollTop    = null
    }

    // (3) Remove the input guards. The capture flag must match the registration
    // for removal to take effect.
    window.removeEventListener("wheel",     this._bound.onWheel,     { capture: true })
    window.removeEventListener("touchmove", this._bound.onTouchMove, { capture: true })
    window.removeEventListener("keydown",   this._bound.onScrollKey, { capture: true })
    document.removeEventListener("touchmove", this._bound.onTouchMove)
  }

  _onTouchMove(event) {
    event.preventDefault()
  }

  // Resolve the element that actually scrolls behind the overlay. On the Live
  // Command Center that's #app-wrapper (see _lockScroll). Falls back to the
  // document scroller on pages without that wrapper (and in the jsdom test
  // harness), so the container lock degrades gracefully.
  _realScrollContainer() {
    return document.getElementById("app-wrapper") ||
           document.scrollingElement ||
           document.documentElement
  }

  // Capturing wheel guard — cancels every wheel/trackpad scroll while the tour
  // is active. Only bound during the lock, so no active-flag check is needed.
  _onWheel(event) {
    event.preventDefault()
  }

  // Capturing keydown guard — cancels the default SCROLL action of scroll keys.
  // It does NOT stopPropagation, so the tooltip's own keyboard handling in
  // _onKey (Tab trap, arrow Next/Back, Escape) still runs. Space is left alone
  // when a tooltip control is focused, where it activates the button rather than
  // scrolling.
  _onScrollKey(event) {
    if (!SCROLL_KEYS.has(event.key)) return
    const isSpace = event.key === " " || event.key === "Spacebar"
    if (isSpace && this._tooltip && this._tooltip.contains(event.target)) return
    event.preventDefault()
  }

  // POST to the server dismissal endpoint so the banner stays gone across
  // sessions/devices. Falls back to localStorage if no URL was provided.
  _dismiss() {
    if (this.dismissalUrlValue) {
      const token = this.csrfTokenValue ||
                    document.querySelector('meta[name="csrf-token"]')?.content ||
                    ""
      fetch(this.dismissalUrlValue, {
        method:  "POST",
        headers: {
          "Content-Type":    "application/x-www-form-urlencoded",
          "X-CSRF-Token":    token,
          "X-Requested-With": "XMLHttpRequest",
          "Accept":          "text/vnd.turbo-stream.html, text/html"
        },
        body: new URLSearchParams({ banner_key: this.bannerKeyValue }).toString()
      }).catch(() => {
        // Non-fatal: if the request fails (e.g. session expired) the banner
        // will just reappear on next page load.
      })
    } else {
      // Fallback: localStorage per-device.
      try {
        localStorage.setItem(`tour_dismissed_${this.bannerKeyValue}`, "1")
      } catch (_e) { /* storage blocked — non-fatal */ }
    }

    // Immediately hide the banner DOM node so it's gone without waiting for
    // the Turbo Stream response.
    const bannerEl = document.getElementById(`onboarding-banner-${this.bannerKeyValue}`)
    if (bannerEl) bannerEl.remove()
  }

  _teardown() {
    document.removeEventListener("keydown", this._bound.onKey)
    window.removeEventListener("resize",   this._bound.onResize)
    this._unlockScroll()

    // Cancel any frame queued by _render so its callback can't run (and
    // dereference nulled refs) after teardown.
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }

    if (this._overlay) {
      this._overlay.remove()
      this._overlay   = null
      this._spotlight = null
      this._tooltip   = null
    }

    // Restore focus so keyboard users aren't stranded on <body>. The trigger
    // CTA is removed on dismiss, so fall back to the dashboard landmark.
    this._restoreFocus()
  }

  _restoreFocus() {
    const target =
      (this._returnFocusEl && this._returnFocusEl.isConnected && this._returnFocusEl) ||
      document.getElementById("live-dashboard-page")
    this._returnFocusEl = null
    if (!target || typeof target.focus !== "function") return
    // A non-interactive landmark needs a tabindex to be focusable.
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1")
    target.focus()
  }

  _onKey(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.skip()
    } else if (event.key === "Tab") {
      this._trapFocus(event)
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      // Scope arrow navigation to the tooltip controls so we don't intercept
      // arrow keys a screen reader uses for browse-mode reading.
      if (this._tooltip && this._tooltip.contains(document.activeElement)) {
        event.preventDefault()
        this.next()
      }
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      if (this._tooltip && this._tooltip.contains(document.activeElement)) {
        event.preventDefault()
        this.back()
      }
    }
  }

  _onResize() {
    if (!this._overlay) return
    const step   = this._steps[this._stepIndex]
    const anchor = document.getElementById(step.anchor)
    if (!anchor) return
    this._positionSpotlight(anchor)
    this._positionTooltip(anchor)
  }

}
